import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-662ec80e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { J as vNumber, S as vOneOf, Q as vBoolean } from './validation-02b8d01d.js';
import { t as tuple } from './types-6f6b2fba.js';
import './i18n-939e2dee.js';

const tabsVersions = tuple('border', 'background');
const tabsOrientations = tuple('horizontal', 'vertical');

const dsTabsCss = "ds-tabs{display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap}ds-tabs slot,ds-tabs slot>*{display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap}ds-tabs[orientation=\"vertical\"] slot{-ms-flex-direction:column;flex-direction:column}ds-tabs[orientation=\"vertical\"] slot ds-tab{display:-ms-flexbox;display:flex;width:100%}ds-tabs[orientation=\"horizontal\"][is-full-width]{width:100%}ds-tabs[orientation=\"horizontal\"][is-full-width] ds-tab{-ms-flex-positive:1;flex-grow:1}ds-tabs ds-tab{font-family:var(--font-family);-webkit-font-smoothing:antialiased;border:none;outline:none;background:none;display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;cursor:pointer;-ms-flex-pack:center;justify-content:center;transition:all 0.1s ease-in-out;font-size:0.88888889rem;line-height:1.56rem}ds-tabs ds-tab slot>*{height:100%;padding:calc(var(--gw) * 1) calc(var(--gw) * 1.7);display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;color:var(--grey-900);font-weight:400}ds-tabs ds-tab[is-disabled] a,ds-tabs ds-tab[is-disabled] button{cursor:not-allowed}ds-tabs ds-icon svg{fill:var(--grey-900)}ds-tabs[version=\"border\"] ds-tab{border-bottom:1px solid var(--grey-500)}ds-tabs[version=\"border\"] ds-tab:not(.active-tab):not([is-disabled]):hover *{color:var(--black);fill:var(--black)}ds-tabs[version=\"border\"] ds-tab.active-tab{border-bottom:2px solid var(--primary)}ds-tabs[version=\"border\"] ds-tab.active-tab *{color:var(--primary);font-weight:500;fill:var(--primary)}ds-tabs[orientation=\"vertical\"][version=\"border\"] ds-tab{-ms-flex-pack:start;justify-content:flex-start;border-right:1px solid var(--grey-500);border-bottom:none;width:100%}ds-tabs[orientation=\"vertical\"][version=\"border\"] .active-tab{border-right:2px solid var(--primary);border-bottom:none}ds-tabs[version=\"background\"] ds-tab{color:var(--grey-800);background-color:var(--grey-100)}ds-tabs[version=\"background\"] ds-tab slot>*{padding:calc(var(--gw) * 0.5) calc(var(--gw) * 1.7)}ds-tabs[version=\"background\"] ds-tab *{color:var(--grey-800);fill:var(--grey-800)}ds-tabs[version=\"background\"] ds-tab:not(.active-tab):not([disabled]):hover{background-color:var(--grey-300)}ds-tabs[version=\"background\"] ds-tab:not(.active-tab):not([disabled]):hover *{color:var(--black);fill:var(--black)}ds-tabs[version=\"background\"] .active-tab{background-color:var(--grey-300)}ds-tabs[version=\"background\"] .active-tab *{color:var(--black);fill:var(--black)}ds-tabs[orientation=\"vertical\"][version=\"background\"] ds-tab{-ms-flex-pack:start;justify-content:flex-start;width:100%}ds-tabs[orientation=\"vertical\"][version=\"background\"] ds-tab slot>*{padding:calc(var(--gw) * 0.8) calc(var(--gw) * 1.7)}";

const DsTabs = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.tabChange = createEvent(this, "tabChange", 7);
    this.propError = {};
    this.currentTab = 0;
    this.orientation = 'horizontal';
    this.version = 'border';
    this.isFullWidth = false;
  }
  watchCurrentTab() {
    /* istanbul ignore if */
    if (!propCheck(this, 'currentTab', 'number', vNumber))
      return;
    const tabs = Array.from(this.host.querySelectorAll('ds-tab'));
    for (let tab of tabs)
      tab.unselect();
    if (tabs[this.currentTab])
      tabs[this.currentTab].select();
  }
  watchOrientation() {
    /* istanbul ignore if */
    if (!propCheck(this, 'orientation', tabsOrientations.join(' | '), vOneOf(tabsOrientations)))
      return;
  }
  watchVersion() {
    /* istanbul ignore if */
    if (!propCheck(this, 'version', tabsVersions.join(' | '), vOneOf(tabsVersions)))
      return;
  }
  watchIsFullWidth() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isFullWidth', 'boolean', vBoolean))
      return;
  }
  matchCurrentOrClosest(el, selector) {
    if (el.matches(selector))
      return el;
    else
      return el.closest(selector);
  }
  async onClick(e) {
    const tabs = Array.from(this.host.querySelectorAll('ds-tab'));
    const index = tabs.findIndex(t => t === this.matchCurrentOrClosest(e.target, 'ds-tab'));
    if (index === -1 || tabs[index].isDisabled)
      return;
    this.tabChange.emit(index);
    for (let tab of tabs)
      await tab.unselect();
    await tabs[index].select();
  }
  connectedCallback() {
    runWatchers(this);
  }
  componentDidLoad() {
    const tabs = Array.from(this.host.querySelectorAll('ds-tab'));
    for (let tab of tabs)
      tab.unselect();
    if (tabs[this.currentTab])
      tabs[this.currentTab].select();
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    return (h(Host, { role: "tablist", onClick: (e) => this.onClick(e) }, h("slot", null)));
  }
  get host() { return getElement(this); }
  static get watchers() { return {
    "currentTab": ["watchCurrentTab"],
    "orientation": ["watchOrientation"],
    "version": ["watchVersion"],
    "isFullWidth": ["watchIsFullWidth"]
  }; }
};
DsTabs.style = dsTabsCss;

export { DsTabs as ds_tabs };
